import { fr } from "date-fns/locale";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from "lucide-react";
import type {
  DayPickerProps,
  PropsBase,
  PropsMulti,
  PropsMultiRequired,
  PropsRange,
  PropsRangeRequired,
  PropsSingle,
  PropsSingleRequired,
} from "react-day-picker";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";

import { buttonVariants } from "./button";

export type Mode = "single" | "multiple" | "range";

export type CalendarProps<TMode extends Mode = Mode> = {
  mode: TMode;
} & PropsBase &
  (TMode extends "single"
    ? PropsSingle | PropsSingleRequired
    : TMode extends "multiple"
      ? PropsMulti | PropsMultiRequired
      : TMode extends "range"
        ? PropsRange | PropsRangeRequired
        : never);

function Calendar<TMode extends Mode = Mode>({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps<TMode>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      locale={fr}
      captionLayout="dropdown"
      navLayout="around"
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        caption_label: "text-sm font-medium flex gap-1 items-center",
        month: "grid grid-cols-[auto_1fr_auto] items-center gap-x-1",
        month_caption:
          "col-start-2 row-start-1 flex h-9 items-center justify-center",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "col-start-1 row-start-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "col-start-3 row-start-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        month_grid: "col-span-3 mt-2 w-full border-collapse",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "rounded-lg h-9 w-9 p-0 text-center text-sm font-normal relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 text-center text-sm font-normal aria-selected:bg-primary [&:has([aria-selected])]:text-primary-foreground"
        ),
        range_end: "day-range-end",
        selected: "bg-primary text-primary-foreground pointer-events-none",
        today: "bg-accent text-accent-foreground",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        dropdowns: "flex items-center gap-1",
        dropdown_root: "relative inline-flex h-8 items-center rounded-md px-1",
        dropdown:
          "absolute inset-0 z-10 w-full cursor-pointer appearance-none border-none bg-transparent opacity-0",
        months_dropdown: "h-8",
        years_dropdown: "h-8",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          switch (orientation) {
            case "left":
              return <ChevronLeft className="h-4 w-4" />;
            case "right":
              return <ChevronRight className="h-4 w-4" />;
            case "up":
              return <ChevronUp className="h-4 w-4" />;
            case "down":
              return <ChevronDown className="h-4 w-4" />;
            default:
              return <ChevronRight className="h-4 w-4" />;
          }
        },
      }}
      {...(props as DayPickerProps)}
    />
  );
}

export { Calendar };
